import { useState } from 'react';
import { IonIcon } from '@ionic/react';
import { star, checkmarkCircle, chevronUp, chevronDown } from 'ionicons/icons';
import { useCreateNpsResponse, useActiveSurvey } from '@/lib/api/hooks';
import './FooterNps.css';

const ZONES = [
  { range: [0, 6], label: 'Needs work', color: '#ef4444' },
  { range: [7, 8], label: "It's okay", color: '#f59e0b' },
  { range: [9, 10], label: 'Love it', color: '#22c55e' },
] as const;

function getZone(score: number) {
  return ZONES.find(z => score >= z.range[0] && score <= z.range[1]) ?? ZONES[0];
}

export function FooterNps() {
  const [expanded, setExpanded] = useState(false);
  const [score, setScore] = useState<number | null>(null);
  const [comment, setComment] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const { data: surveyEnvelope } = useActiveSurvey('client');
  const createResponse = useCreateNpsResponse();

  const surveyId = surveyEnvelope?.data?._id;
  const zone = score !== null ? getZone(score) : null;

  const handleSubmit = async () => {
    if (score === null || !surveyId) return;
    await createResponse.mutateAsync({
      survey: { type: 'npssurvey', id: surveyId },
      subject: { type: 'client', id: 'platform' },
      score,
      comment: comment.trim() || undefined,
    });
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="bp-footer-nps">
        <div className="bp-footer-nps-thanks">
          <IonIcon icon={checkmarkCircle} className="bp-footer-nps-thanks-icon" />
          <span>Thank you for your feedback!</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bp-footer-nps">
      <button
        type="button"
        className="bp-footer-nps-toggle"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
      >
        <span className="bp-footer-nps-question">How likely are you to recommend BluePages?</span>
        <IonIcon icon={expanded ? chevronDown : chevronUp} className="bp-footer-nps-chevron" />
      </button>

      {expanded && (
        <div className="bp-footer-nps-body">
          <div className="bp-footer-nps-scale-labels">
            <span>Not likely</span>
            <span>Very likely</span>
          </div>

          <div className="bp-footer-nps-scale">
            {Array.from({ length: 11 }, (_, i) => {
              const z = getZone(i);
              const isSelected = score === i;
              return (
                <button
                  key={i}
                  type="button"
                  className={`bp-footer-nps-btn ${isSelected ? 'is-selected' : ''}`}
                  style={{
                    '--btn-color': z.color,
                    ...(isSelected ? { background: z.color, borderColor: z.color, color: '#fff' } : {}),
                  } as React.CSSProperties}
                  onClick={() => setScore(i)}
                  aria-label={`Score ${i}`}
                >
                  {i}
                </button>
              );
            })}
          </div>

          {zone && (
            <div className="bp-footer-nps-zone" style={{ color: zone.color }}>
              <span className="bp-footer-nps-zone-dot" style={{ background: zone.color }} />
              {zone.label}
            </div>
          )}

          <div className="bp-footer-nps-comment-row">
            <input
              type="text"
              className="bp-footer-nps-comment"
              placeholder="Anything you'd like to share? (optional)"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && score !== null) handleSubmit(); }}
            />
            <button
              type="button"
              className="bp-footer-nps-submit"
              disabled={score === null || !surveyId || createResponse.isPending}
              onClick={handleSubmit}
            >
              {createResponse.isPending ? (
                <span className="bp-spinner bp-spinner--sm" />
              ) : (
                <>
                  <IonIcon icon={star} />
                  Submit
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
